import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  Maybe,
  PinwheelExperienceInput,
  VxlAction,
  VxlActionTypenames,
} from '@varo/graphql-schema-types';
import { Href, useFocusEffect, useRouter } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { navigateToDirectDepositSetup } from 'features/directDeposit/navigation/navigateToDirectDepositSetup';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import useMoveMoneyPageTiles from 'features/moveMoney/hooks/useMoveMoneyPageTile';
import TransferHistoryBottomSheet from 'features/moveMoney/screens/transferHistoryBottomSheet/TransferHistoryBottomSheet';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import ExpressiveSmText from 'shared/components/vxl/typography/expressive/ExpressiveSmText';
import { isWeb } from 'shared/constants/platform';
import SectionedContentScreen from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDirectDepositGateway } from 'shared/hooks/pinwheel/useDirectDepositGateway/useDirectDepositGateway';
import useDirectDepositLoadingComponent from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import TabDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/TabDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

function MoveMoneyScreen() {
  const router = useRouter();
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const { segmentClient } = useAnalyticsConfiguration();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'move',
  });

  const { refetchDdManagementFlow, handleDdManagementLinkTokenFailure } =
    useDirectDepositManagementFlow({
      pause: true,
    });

  const { initiatePinwheel, isPinwheelLoading: pinWheelLoading } =
    usePinwheel();
  const { skipIntroScreen } = useDirectDepositLoadingComponent();
  useViewDidFocusScreenAnalyticsEvent({
    key: 'moveMoneyScreen',
  });

  const handleHeaderPress = () => {
    void segmentClient.logEvent('V2A Clicked', {
      source_name: 'move_money_promotile',
      category: 'v2a',
      subcategory: 'transfer_money_v2a_sender',
    });
  };
  const { directDepositGatewayFlag, abCohort } = useDirectDepositGateway();

  const handleTileOnPress = (action: Maybe<VxlAction> | undefined) => {
    if (action?.__typename === VxlActionTypenames.RouteNavigationAction) {
      router.push(action.route as Href);
    } else if (
      action?.__typename === VxlActionTypenames.PresentClientBottomSheetAction
    ) {
      bottomSheetRef?.current?.present();
    } else if (
      action?.__typename === VxlActionTypenames.LaunchPinwheelFlowAction
    ) {
      if (action.shouldLaunchPinwheelSdk) {
        const handleInitiatePinwheel = () => {
          initiatePinwheel({
            input: {
              experience: PinwheelExperienceInput.DirectDepositSwitch,
              skipIntroScreen,
            },
            launchBehavior: skipIntroScreen
              ? { delayAfterTokenMs: 3000 }
              : undefined,
            onOutcome: refetchDdManagementFlow,
            onLinkTokenError: handleDdManagementLinkTokenFailure,
          });
        };
        if (directDepositGatewayFlag) {
          navigateToDirectDepositSetup({
            entry: 'move-money',
            sourceName: 'move_money_menu',
            abCohort,
            isCoinLoader: false,
          });
          return;
        }
        handleInitiatePinwheel();
      } else {
        router.push('/move-money/direct-deposit/direct-deposit-management');
      }
    }
    if (action?.analyticsEvent) {
      const analyticsEvent = action.analyticsEvent;
      const eventName = analyticsEvent.name ?? '';
      const eventProperties =
        analyticsEvent.properties?.reduce(
          (result, property) => {
            result[property.key] = property.value;
            return result;
          },
          {} as Record<string, string>,
        ) ?? {};
      void segmentClient.logEvent(eventName, eventProperties);
    }
  };

  const { moveMoneyPageTiles, isLoading, error, reload } =
    useMoveMoneyPageTiles(handleTileOnPress, handleHeaderPress);

  useFocusEffect(
    useCallback(() => {
      reload();
    }, [reload]),
  );

  const onPressInfoIcon = () => router.push('/move-money/compare-options');

  return (
    <>
      <StatusBar style="dark" />
      <View style={styles.container}>
        <NavigationBar
          title=""
          empty
          trailingAccessories={
            <View style={styles.buttonWrapper}>
              <IconButton
                iconKey="INFO"
                iconSize="MD"
                iconButtonAccessibilityLabel="Info"
                iconOnPress={onPressInfoIcon}
                analyticsId="move_money_options"
              />
            </View>
          }
        />
        {moveMoneyPageTiles?.sectionList ? (
          <SectionedContentScreen
            model={{
              header: {
                id: 'moveMoneyHeader',
                render: () => (
                  <ExpressiveSmText
                    testID="nav-title"
                    style={styles.headerText}
                    numberOfLines={1}
                  >
                    {translatedText('title')}
                  </ExpressiveSmText>
                ),
              },
              ...moveMoneyPageTiles.sectionList,
            }}
          />
        ) : null}
        <TransferHistoryBottomSheet bottomSheetRef={bottomSheetRef} />
        <ScreenTransitionOverlay
          isTabPage
          isLoading={isLoading || pinWheelLoading}
          LoadingComponent={
            <DirectDepositLoader
              isLoadingConditions={isLoading}
              isPinwheelLoading={pinWheelLoading}
            />
          }
          ErrorComponent={
            error ? (
              <TabDownErrorMessage error={error} tryAgainAction={reload} />
            ) : null
          }
        />
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  // This helps with the layout of the heading so its not pushed down by a few pixels
  // Likely being caused by the icon being too tall
  buttonWrapper: { height: 20 },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingTop: isWeb ? 12 : 0,
  },
  headerText: {
    paddingHorizontal: theme.spacing.m,
  },
});

export default MoveMoneyScreen;
